import { api } from '@/lib/api';

export interface ActivityLog {
  id: string;
  type: string;
  message: string;
  area: string;
  timestamp: string;
  read: boolean;
  details?: any;
  userId?: string;
}

export interface CreateActivityRequest {
  type: string;
  message: string;
  area: string;
  details?: any;
  userId?: string;
}

export interface UpdateActivityRequest {
  read?: boolean;
}

class ActivityDatabaseService {
  private baseUrl = '/api/activities';

  // Get all activities with optional filtering
  async getActivities(filters?: {
    type?: string;
    area?: string;
    read?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<ActivityLog[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.type) params.append('type', filters.type);
      if (filters?.area) params.append('area', filters.area);
      if (filters?.read !== undefined) params.append('read', filters.read.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const response = await api.get(`${this.baseUrl}?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching activities:', error);
      return [];
    }
  }

  // Create a new activity
  async createActivity(activity: CreateActivityRequest): Promise<ActivityLog | null> {
    try {
      const response = await api.post(this.baseUrl, activity);
      return response.data;
    } catch (error) {
      console.error('Error creating activity:', error);
      return null;
    }
  }

  // Update an activity (e.g., mark as read/unread)
  async updateActivity(id: string, updates: UpdateActivityRequest): Promise<ActivityLog | null> {
    try {
      const response = await api.put(`${this.baseUrl}/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating activity:', error);
      return null;
    }
  }

  // Delete a specific activity
  async deleteActivity(id: string): Promise<boolean> {
    try {
      await api.delete(`${this.baseUrl}/${id}`);
      return true;
    } catch (error) {
      console.error('Error deleting activity:', error);
      return false;
    }
  }

  // Clear all activities
  async clearAllActivities(): Promise<boolean> {
    try {
      await api.delete(this.baseUrl);
      return true;
    } catch (error) {
      console.error('Error clearing all activities:', error);
      return false;
    }
  }

  // Get activity statistics
  async getActivityStats(): Promise<{
    total: number;
    unread: number;
    byType: Record<string, number>;
    byArea: Record<string, number>;
  }> {
    try {
      const response = await api.get(`${this.baseUrl}/stats`);
      return response.data;
    } catch (error) {
      console.error('Error fetching activity stats:', error);
      return {
        total: 0,
        unread: 0,
        byType: {},
        byArea: {}
      };
    }
  }

  // Mark all activities as read
  async markAllAsRead(): Promise<boolean> {
    try {
      await api.put(`${this.baseUrl}/mark-all-read`);
      return true;
    } catch (error) {
      console.error('Error marking all activities as read:', error);
      return false;
    }
  }
}

export const activityDatabaseService = new ActivityDatabaseService();
